/**

* Handles filtering for collections with >5000 products
 * Uses Storefront API but maintains native Shopify UI/UX
 */

class LargeCollectionFilters {
  constructor() {
    this.isLargeCollection = false;
    this.collectionHandle = null;
    this.apiClient = null;
    this.currentFilters = [];
    this.currentSort = 'manual';
    this.currentPage = 1;
    this.pageInfo = null;
    this.productsPerPage = 24;
    this.totalPages = null;
    this.init();
  }

  init() {
    // Check if this is a large collection that needs custom filtering
    const collectionMeta = document.querySelector('[data-collection-meta]');
    if (!collectionMeta) return;

    const productCount = parseInt(collectionMeta.dataset.productCount || '0');
    this.collectionHandle = collectionMeta.dataset.collectionHandle;
    
    console.log('Large Collection Filters: Detected', productCount, 'products');
    
    // Only activate for collections with >5000 products
    if (productCount > 5000 && this.collectionHandle) {
      this.isLargeCollection = true;
      
      // Calculate estimated total pages (will be updated when filters are applied)
      this.totalPages = Math.ceil(productCount / this.productsPerPage);
      
      // Check if API token is configured
      if (!window.storefrontApiToken || window.storefrontApiToken === '') {
        console.error('⚠️ Storefront API token is missing! Please configure it in Theme Settings > Advanced Settings');
        this.showError('Storefront API token not configured. Please contact store administrator.');
        return;
      }
      
      console.log('✓ Initializing custom filtering for large collection');
      this.apiClient = new StorefrontFilters(this.collectionHandle);
      this.loadFilters();
      this.attachEventListeners();
    }
  }
  
  showError(message) {
    const loadingPlaceholder = document.querySelector('.loading-filters');
    if (loadingPlaceholder) {
      loadingPlaceholder.innerHTML = `
        <div style="padding: 2rem; text-align: center; color: #d32f2f;">
          <p><strong>Configuration Required</strong></p>
          <p style="font-size: 0.9em; margin-top: 0.5rem;">${message}</p>
        </div>
      `;
    }
  }

  /**
   * Load filters from Storefront API and inject into existing UI
   */
  async loadFilters() {
    console.log('Fetching filters from Storefront API...');
    const filters = await this.apiClient.fetchFilters();
    
    if (!filters || filters.length === 0) {
      console.warn('No filters available for large collection');
      this.showError('Unable to load filters. Please check API configuration.');
      return;
    }

    console.log('✓ Filters loaded:', filters.length, 'filter groups');
    this.renderFilters(filters);
    
    // Parse URL parameters and apply initial filters
    this.applyFiltersFromURL(filters);
  }

  /**
   * Parse URL parameters and apply filters from URL
   */
  applyFiltersFromURL(availableFilters) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = [];
    
    // Parse all filter.* parameters from URL
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.')) {
        // Parse filter key: filter.p.product_type, filter.p.vendor, filter.p.tag, filter.v.option.*, filter.p.m.*
        const filterParts = key.split('.');
        
        if (filterParts.length >= 3) {
          const filterCategory = filterParts[1]; // 'p' for product, 'v' for variant
          const filterType = filterParts.slice(2).join('.'); // product_type, vendor, tag, option.color, m.custom.author
          
          console.log(`URL Filter found: ${key} = ${value}`);
          
          // Build filter input based on type
          let filterInput = null;
          
          if (filterType === 'product_type') {
            filterInput = { productType: value };
          } else if (filterType === 'vendor') {
            filterInput = { productVendor: value };
          } else if (filterType === 'tag') {
            filterInput = { tag: value };
          } else if (filterType.startsWith('m.')) {
            // Metafield filter: filter.p.m.custom.author
            const metafieldParts = filterType.substring(2).split('.');
            if (metafieldParts.length >= 2) {
              filterInput = {
                productMetafield: {
                  namespace: metafieldParts[0],
                  key: metafieldParts[1],
                  value: value
                }
              };
            }
          } else if (filterType.startsWith('option.')) {
            // Variant option filter: filter.v.option.color
            const optionName = filterType.substring(7);
            filterInput = {
              variantOption: {
                name: optionName,
                value: value
              }
            };
          }
          
          if (filterInput) {
            urlFilters.push({
              key: filterType,
              type: filterCategory,
              input: filterInput,
              rawValue: value
            });
          }
        }
      }
    }
    
    // Parse sort_by parameter
    const sortBy = urlParams.get('sort_by');
    if (sortBy) {
      this.currentSort = sortBy;
      console.log(`URL Sort found: ${sortBy}`);
    }
    
    if (urlFilters.length > 0) {
      console.log('✓ Applying', urlFilters.length, 'filters from URL');
      
      // Check corresponding checkboxes in the UI
      this.checkFiltersInUI(urlFilters, availableFilters);
      
      // Apply the filters - store as objects for direct API use
      this.currentFilters = urlFilters.map(f => ({
        key: f.key,
        type: f.type,
        values: [JSON.stringify(f.input)], // Store as JSON string for buildFilterInput compatibility
      }));
      
      // Fetch filtered products
      this.fetchFilteredProducts();
    }
  }

  /**
   * Check the corresponding filter checkboxes in the UI based on URL params
   */
  checkFiltersInUI(urlFilters, availableFilters) {
    urlFilters.forEach(urlFilter => {
      // Find matching filter in available filters
      availableFilters.forEach(filter => {
        filter.values.forEach((value, vIndex) => {
          try {
            const inputData = JSON.parse(value.input);
            
            // Check if this filter value matches the URL filter
            let matches = false;
            
            if (urlFilter.input.productType && inputData.productType === urlFilter.input.productType) {
              matches = true;
            } else if (urlFilter.input.productVendor && inputData.productVendor === urlFilter.input.productVendor) {
              matches = true;
            } else if (urlFilter.input.tag && inputData.tag === urlFilter.input.tag) {
              matches = true;
            } else if (urlFilter.input.productMetafield && inputData.productMetafield) {
              if (inputData.productMetafield.namespace === urlFilter.input.productMetafield.namespace &&
                  inputData.productMetafield.key === urlFilter.input.productMetafield.key &&
                  inputData.productMetafield.value === urlFilter.input.productMetafield.value) {
                matches = true;
              }
            } else if (urlFilter.input.variantOption && inputData.variantOption) {
              if (inputData.variantOption.name === urlFilter.input.variantOption.name &&
                  inputData.variantOption.value === urlFilter.input.variantOption.value) {
                matches = true;
              }
            }
            
            if (matches) {
              // Check the checkbox in both desktop and mobile
              const desktopCheckbox = document.querySelector(`#Filter-${filter.id}-${vIndex}`);
              const mobileCheckbox = document.querySelector(`#Filter-${filter.id}-mobile-${vIndex}`);
              
              if (desktopCheckbox) {
                desktopCheckbox.checked = true;
                console.log(`✓ Checked desktop filter: ${filter.label} - ${value.label}`);
              }
              if (mobileCheckbox) {
                mobileCheckbox.checked = true;
                console.log(`✓ Checked mobile filter: ${filter.label} - ${value.label}`);
              }
            }
          } catch (e) {
            // Skip if JSON parsing fails
          }
        });
      });
    });
  }

  /**
   * Fetch filtered products (extracted for reuse)
   */
  async fetchFilteredProducts() {
    this.showLoadingState();
    
    const sortKey = StorefrontFilters.getSortKey(this.currentSort);
    
    // Build filter inputs - handle both raw objects and JSON strings
    const filterInputs = this.currentFilters.flatMap(f => {
      return f.values.map(v => {
        // If it's already an object, return it
        if (typeof v === 'object') return v;
        // Otherwise try to parse it as JSON
        try {
          return JSON.parse(v);
        } catch (e) {
          return null;
        }
      }).filter(Boolean);
    });
    
    console.log('Fetching products with filters:', filterInputs);
    
    // Fetch products and total count in parallel
    const [products, totalCount] = await Promise.all([
      this.apiClient.fetchProducts(filterInputs, sortKey, this.productsPerPage),
      this.apiClient.fetchFilteredCount(filterInputs)
    ]);
    
    if (products) {
      this.pageInfo = products.pageInfo;
      this.filteredTotalCount = totalCount;
      this.renderProducts(products);
      this.updateProductCount(totalCount || products.edges.length);
      this.renderPagination(products);
    }
    
    this.hideLoadingState();
  }

  /**
   * Render filters into the existing native Shopify filter containers
   */
  renderFilters(filters) {
    const desktopContainer = document.querySelector('#FacetsWrapperDesktop');
    const mobileContainer = document.querySelector('.mobile-facets__main');

    console.log('Rendering filters - Desktop container:', !!desktopContainer, 'Mobile container:', !!mobileContainer);

    if (!desktopContainer && !mobileContainer) {
      console.warn('No filter containers found!');
      return;
    }

    // Remove loading placeholder
    const loadingPlaceholder = document.querySelector('.loading-filters');
    if (loadingPlaceholder) {
      loadingPlaceholder.remove();
    }

    // Clear loading state
    document.querySelectorAll('.loading__spinner').forEach(el => el.classList.add('hidden'));

    filters.forEach((filter, index) => {
      // Skip vendor and tag filters as per native behavior
      if (filter.label === 'Vendor' || filter.label === 'Tags') return;

      if (desktopContainer) {
        this.renderDesktopFilter(filter, index, desktopContainer);
      }
      if (mobileContainer) {
        this.renderMobileFilter(filter, index, mobileContainer);
      }
    });
    
    console.log('✓ Filters rendered successfully');
  }

  /**
   * Render desktop filter (mimics native Shopify structure)
   */
  renderDesktopFilter(filter, index, container) {
    const detailsEl = document.createElement('details');
    detailsEl.className = 'facets__disclosure-vertical js-filter';
    detailsEl.dataset.index = index;
    if (index === 0) detailsEl.open = true;

    const summaryHTML = `
      <summary class="facets__summary caption-large focus-offset">
        <div>
          <span class="facets__summary-label">
            ${filter.label}
            <span class="facets__selected no-js-hidden hidden">(0)</span>
          </span>
          <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
          </svg>
        </div>
      </summary>
    `;

    const valuesHTML = filter.values.map((value, vIndex) => `
      <li class="list-menu__item facets__item">
        <label for="Filter-${filter.id}-${vIndex}" class="facets__label facet-checkbox${value.count === 0 ? ' facet-checkbox--disabled disabled' : ''}">
          <input type="checkbox" 
                 name="filter.v.${filter.type}.${filter.id}" 
                 value="${value.input}"
                 id="Filter-${filter.id}-${vIndex}"
                 data-filter-type="${filter.type}"
                 data-filter-id="${filter.id}"
                 ${value.count === 0 ? 'disabled' : ''}>
          <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>
          </svg>
          <svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none">
            <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="facet-checkbox__text">${value.label} (${value.count})</span>
        </label>
      </li>
    `).join('');

    const contentHTML = `
      <div class="facets__display-vertical">
        <fieldset class="facets-wrap facets-wrap-vertical">
          <legend class="visually-hidden">${filter.label}</legend>
          <ul class="facets-layout-list facets__list--vertical list-unstyled no-js-hidden" role="list">
            ${valuesHTML}
          </ul>
        </fieldset>
      </div>
    `;

    detailsEl.innerHTML = summaryHTML + contentHTML;
    container.appendChild(detailsEl);
  }

  /**
   * Escape HTML entities for safe insertion into attributes
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render mobile filter (mimics native Shopify structure)
   */
  renderMobileFilter(filter, index, container) {
    console.log(`Rendering mobile filter: ${filter.label} with ${filter.values.length} values`);
    
    const detailsEl = document.createElement('details');
    detailsEl.className = 'mobile-facets__details js-filter';
    detailsEl.dataset.index = `mobile-${index}`;

    // Create filter values list
    const valuesList = document.createElement('ul');
    valuesList.className = 'facets-layout-list mobile-facets__list list-unstyled';
    valuesList.setAttribute('role', 'list');
    
    filter.values.forEach((value, vIndex) => {
      const li = document.createElement('li');
      li.className = 'mobile-facets__item list-menu__item';
      
      const label = document.createElement('label');
      label.htmlFor = `Filter-${filter.id}-mobile-${vIndex}`;
      label.className = `facets__label mobile-facets__label${value.count === 0 ? ' mobile-facets__label--disabled disabled' : ''}`;
      
      const input = document.createElement('input');
      input.className = 'mobile-facets__checkbox';
      input.type = 'checkbox';
      input.name = `filter.v.${filter.type}.${filter.id}`;
      input.value = value.input; // Set value directly without escaping
      input.id = `Filter-${filter.id}-mobile-${vIndex}`;
      input.dataset.filterType = filter.type;
      input.dataset.filterId = filter.id;
      if (value.count === 0) input.disabled = true;
      
      label.appendChild(input);
      label.innerHTML += `
        <span class="mobile-facets__highlight"></span>
        <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>
        </svg>
        <svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none">
          <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span class="facet-checkbox__text">${value.label} (${value.count})</span>
      `;
      
      li.appendChild(label);
      valuesList.appendChild(li);
    });

    detailsEl.innerHTML = `
      <summary class="mobile-facets__summary focus-inset" role="button" aria-expanded="false" aria-controls="FacetMobile-${index}">
        <div>
          <span>${filter.label}</span>
          <span class="mobile-facets__arrow no-js-hidden">
            <svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path>
            </svg>
          </span>
        </div>
      </summary>
      <div id="FacetMobile-${index}" class="mobile-facets__submenu gradient">
        <button class="mobile-facets__close-button link link--text focus-inset" aria-expanded="true" type="button">
          <svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path>
          </svg>
          <span>${filter.label}</span>
        </button>
      </div>
    `;
    
    // Append the programmatically created values list to avoid HTML escaping issues
    const submenu = detailsEl.querySelector('.mobile-facets__submenu');
    submenu.appendChild(valuesList);

    const footer = container.querySelector('.mobile-facets__footer');
    console.log(`Inserting ${filter.label} before footer:`, !!footer);
    
    if (footer) {
      container.insertBefore(detailsEl, footer);
    } else {
      container.appendChild(detailsEl);
    }
    
    // Add event listener to handle opening/closing (mimic native Shopify behavior)
    const summary = detailsEl.querySelector('summary');
    summary.addEventListener('click', (event) => {
      const isCurrentlyOpen = detailsEl.hasAttribute('open');
      
      // Update aria-expanded
      summary.setAttribute('aria-expanded', !isCurrentlyOpen);
      
      // Add/remove menu-opening class
      if (!isCurrentlyOpen) {
        setTimeout(() => {
          detailsEl.classList.add('menu-opening');
        }, 10);
      } else {
        detailsEl.classList.remove('menu-opening');
      }
    });
    
    console.log(`✓ ${filter.label} filter inserted with event listeners`);
  }

  /**
   * Attach event listeners to filter inputs
   */
  attachEventListeners() {
    document.addEventListener('change', (e) => {
      if (!this.isLargeCollection) return;
      
      const filterInput = e.target.closest('input[data-filter-id]');
      if (filterInput) {
        this.handleFilterChange();
      }
    });

    // Handle sort changes
    document.addEventListener('change', (e) => {
      if (!this.isLargeCollection) return;
      
      if (e.target.matches('select[name="sort_by"]')) {
        this.currentSort = e.target.value;
        this.handleFilterChange();
      }
    });

    // Handle mobile filter close buttons
    document.addEventListener('click', (e) => {
      if (!this.isLargeCollection) return;
      
      const closeButton = e.target.closest('.mobile-facets__close-button');
      if (closeButton) {
        const details = closeButton.closest('details');
        if (details) {
          details.open = false;
        }
      }
    });
  }

  /**
   * Handle filter changes and fetch filtered products
   */
  async handleFilterChange() {
    // Collect active filters
    this.currentFilters = [];
    const checkedInputs = document.querySelectorAll('input[data-filter-id]:checked');
    
    checkedInputs.forEach(input => {
      const filterId = input.dataset.filterId;
      const filterType = input.dataset.filterType;
      const value = input.value;

      const existing = this.currentFilters.find(f => f.key === filterId);
      if (existing) {
        existing.values.push(value);
      } else {
        this.currentFilters.push({
          key: filterId,
          type: filterType,
          values: [value]
        });
      }
    });

    // Reset to page 1 when filters change
    this.currentPage = 1;

    // Show loading state
    this.showLoadingState();

    // Fetch filtered products and total count in parallel
    const sortKey = StorefrontFilters.getSortKey(this.currentSort);
    const [products, totalCount] = await Promise.all([
      this.apiClient.fetchProducts(this.currentFilters, sortKey, this.productsPerPage),
      this.apiClient.fetchFilteredCount(this.currentFilters)
    ]);

    if (products) {
      this.pageInfo = products.pageInfo;
      this.filteredTotalCount = totalCount;
      this.renderProducts(products);
      this.updateProductCount(totalCount || products.edges.length);
      this.renderPagination(products);
    }

    this.hideLoadingState();
  }

  /**
   * Render products into the grid (reuses native product card template)
   */
  renderProducts(productsData) {
    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;

    // Mark grid as using custom filtering to prevent native JS from overriding
    productGrid.dataset.customFiltering = 'true';
    
    // Clear existing products
    productGrid.innerHTML = '';

    productsData.edges.forEach(({ node: product }) => {
      const li = document.createElement('li');
      li.className = 'grid__item';

      // Prefer the full shared card (image, price, sold-out/badge, add-to-cart)
      // from storefront-search.js; fall back to the simple card if unavailable.
      if (window.StorefrontSearch && window.StorefrontSearch.cardHTML) {
        li.innerHTML = window.StorefrontSearch.cardHTML(window.StorefrontSearch.normalize(product));
      } else {
        li.innerHTML = this.generateProductCardHTML(product);
      }
      productGrid.appendChild(li);
    });
    
    console.log(`✓ Rendered ${productsData.edges.length} products`);
  }

  /**
   * Generate product card HTML (simplified version matching native structure)
   */
  generateProductCardHTML(product) {
    const image = product.images.edges[0]?.node;
    const price = product.priceRange.minVariantPrice;
    
    return `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media" style="--ratio-percent: 100%;">
          <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: 100%;">
            ${image ? `
            <div class="card__media">
              <a href="/products/${product.handle}">
                <div class="media media--transparent media--hover-effect">
                  <img 
                    src="${image.url}" 
                    alt="${image.altText || product.title}" 
                    loading="lazy" 
                    width="${image.width}" 
                    height="${image.height}"
                    class="motion-reduce">
                </div>
              </a>
            </div>
            ` : ''}
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="/products/${product.handle}" class="full-unstyled-link">${product.title}</a>
                </h3>
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h2">
                <a href="/products/${product.handle}" class="full-unstyled-link">${product.title}</a>
              </h3>
              <div class="card-information">
                ${product.vendor ? `
                <span class="visually-hidden">ספק:</span>
                <div class="caption-with-letter-spacing">${product.vendor}</div>
                ` : ''}
                <span class="caption-large light"></span>
                <div class="price">
                  <div class="price__container">
                    <div class="price__title">מחיר</div>
                    <div class="price__inner">
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">מחיר רגיל</span>
                        <span class="price-item price-item--regular">${price.amount} ₪</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Update product count display
   */
  updateProductCount(totalCount) {
    const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
    
    countElements.forEach(el => {
      el.textContent = `${totalCount} מוצרים`;
    });
    
    // Also update the collection banner count
    const bannerCount = document.querySelector('#CollectionProductCount');
    if (bannerCount) {
      bannerCount.textContent = totalCount;
    }
    
    console.log('✓ Updated product count:', totalCount);
  }

  /**
   * Render pagination controls
   */
  renderPagination(productsData) {
    const paginationWrapper = document.querySelector('.pagination-wrapper');
    if (!paginationWrapper) return;

    const pageInfo = productsData.pageInfo;
    
    // If no next or previous page, hide pagination
    if (!pageInfo.hasNextPage && !pageInfo.hasPreviousPage) {
      paginationWrapper.style.display = 'none';
      return;
    }

    paginationWrapper.style.display = 'block';

    // Generate page numbers (current page ± 1)
    const pageNumbers = [];
    if (this.currentPage > 1) {
      pageNumbers.push(this.currentPage - 1);
    }
    pageNumbers.push(this.currentPage);
    if (pageInfo.hasNextPage) {
      pageNumbers.push(this.currentPage + 1);
    }

    // Show ellipsis only when there are more pages (we can't know exact count with filters)
    const showEllipsis = pageInfo.hasNextPage && this.currentFilters.length === 0 && this.totalPages && (this.currentPage + 2) < this.totalPages;

    const paginationHTML = `
      <nav class="pagination" role="navigation" aria-label="עמודים">
        <ul class="pagination__list list-unstyled" role="list">
          ${pageInfo.hasPreviousPage ? `
          <li>
            <a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" 
               aria-label="עמוד קודם" data-page-action="prev">
              <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
              </svg>
            </a>
          </li>
          ` : ''}
          ${pageNumbers.map(pageNum => {
            if (pageNum === this.currentPage) {
              return `
              <li>
                <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" 
                   aria-current="page" aria-label="עמוד ${pageNum}">${pageNum}</a>
              </li>`;
            } else {
              return `
              <li>
                <a href="#" class="pagination__item link" 
                   aria-label="עמוד ${pageNum}" data-page-action="goto" data-page-number="${pageNum}">${pageNum}</a>
              </li>`;
            }
          }).join('')}
          ${showEllipsis ? `
          <li><span class="pagination__item">…</span></li>
          <li>
            <a href="#" class="pagination__item link" 
               aria-label="עמוד ${this.totalPages}" data-page-action="last">${this.totalPages}</a>
          </li>
          ` : pageInfo.hasNextPage && this.currentFilters.length > 0 ? `
          <li><span class="pagination__item">…</span></li>
          ` : ''}
          ${pageInfo.hasNextPage ? `
          <li>
            <a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" 
               aria-label="עמוד הבא" data-page-action="next">
              <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
              </svg>
            </a>
          </li>
          ` : ''}
        </ul>
      </nav>
    `;

    paginationWrapper.innerHTML = paginationHTML;

    // Attach pagination click handlers
    this.attachPaginationListeners();
  }

  /**
   * Attach event listeners to pagination buttons
   */
  attachPaginationListeners() {
    document.querySelectorAll('[data-page-action]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const action = e.currentTarget.dataset.pageAction;
        
        if (action === 'next' && this.pageInfo.hasNextPage) {
          this.currentPage++;
          await this.loadPage(this.pageInfo.endCursor, 'after');
        } else if (action === 'prev' && this.pageInfo.hasPreviousPage) {
          this.currentPage--;
          await this.loadPage(this.pageInfo.startCursor, 'before');
        } else if (action === 'goto') {
          const targetPage = parseInt(e.currentTarget.dataset.pageNumber);
          if (targetPage > this.currentPage && this.pageInfo.hasNextPage) {
            // Going forward
            this.currentPage = targetPage;
            await this.loadPage(this.pageInfo.endCursor, 'after');
          } else if (targetPage < this.currentPage && this.pageInfo.hasPreviousPage) {
            // Going backward
            this.currentPage = targetPage;
            await this.loadPage(this.pageInfo.startCursor, 'before');
          }
        }

        // Scroll to top of product grid
        document.querySelector('#product-grid')?.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  /**
   * Load a specific page of products
   */
  async loadPage(cursor, direction) {
    this.showLoadingState();

    const sortKey = StorefrontFilters.getSortKey(this.currentSort);
    const products = await this.apiClient.fetchProducts(
      this.currentFilters, 
      sortKey, 
      this.productsPerPage,
      cursor,
      direction
    );

    if (products) {
      this.pageInfo = products.pageInfo;
      this.renderProducts(products);
      this.renderPagination(products);
    }

    this.hideLoadingState();
  }

  showLoadingState() {
    document.querySelector('#ProductGridContainer .collection')?.classList.add('loading');
    document.querySelectorAll('.loading__spinner').forEach(el => el.classList.remove('hidden'));
  }

  hideLoadingState() {
    document.querySelector('#ProductGridContainer .collection')?.classList.remove('loading');
    document.querySelectorAll('.loading__spinner').forEach(el => el.classList.add('hidden'));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LargeCollectionFilters();
  });
} else {
  new LargeCollectionFilters();
}
